'use client';

/**
 * API namespace `admin.market.bundles` (sub-namespace extracted).
 * Verbatim extraction from frontend/src/lib/api-client.ts (originally within admin.market).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2 (relative to admin.market), T-EXPORT, T-IMPORTS.
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';
import type {
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
} from '../core/types';
import type {
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  Paginated,
} from '../core/shared-types';

export const bundles = {
  /**
   * GET /api/admin/market/bundles — admin list of ALL bundles across
   * ALL sellers (paginated, 1-indexed). Returns
   * `ProductBundleListResponse` (`bundles: ProductBundleResponse[]`
   * + `total` + `page` + `limit`). Each bundle in the list is the
   * EXPANDED snapshot (includes the `products[]` array with title +
   * priceCredits + thumbnail URL per product, plus the seller's
   * storeName + storeSlug snapshot — same wire shape as the public
   * catalog response per SPEC-242 §4.4).
   *
   * Query params (all optional):
   *   - `page`             (default 50, max 100) — page number.
   *   - `limit`            (default 50, max 100) — page size.
   *   - `status`           — one of `ProductBundleStatus` (5 values:
   *                          pending_approval / approved / rejected /
   *                          paused / out_of_stock). Invalid values
   *                          are silently ignored by the backend.
   *   - `sellerId`         — filter by Seller.sellerId (UUID). Invalid
   *                          values are passed through to the service
   *                          (returns 0 results for unknown sellerIds).
   *   - `includeInactive`  (default true) — when 'false', excludes
   *                          soft-deleted bundles.
   *
   * Authz: `requireAuth` + `requireAdminRead` (router-level). NO
   * 2FA required for the GET (read-only — INV-SEC-06 §9.6 only
   * applies to financial-grade state transitions).
   *
   * Spec ref: SPEC-242 §4.4 (GET /admin/market/bundles), §4.5.2
   *           (admin UI — table of bundles awaiting moderation).
   */
  async list(
    filters?: {
      page?: number;
      limit?: number;
      status?: ProductBundleStatus;
      sellerId?: string;
      includeInactive?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<ProductBundleListResponse> {
    const qs = new URLSearchParams();
    if (filters?.page !== undefined) qs.set('page', String(filters.page));
    if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.sellerId) qs.set('sellerId', filters.sellerId);
    if (filters?.includeInactive !== undefined) {
      qs.set('includeInactive', String(filters.includeInactive));
    }
    const query = qs.toString();
    return apiFetch<ProductBundleListResponse>(
      query ? `/admin/market/bundles?${query}` : '/admin/market/bundles',
      { signal },
    );
  },
  /**
   * PUT /api/admin/market/bundles/:id/approve — approve a pending
   * bundle (INV-MKT-20). The backend atomically transitions
   * `status: 'pending_approval' → 'approved'` via CAS
   * (`findOneAndUpdate({ bundleId, status: 'pending_approval' })`).
   * If the CAS filter fails (another admin rejected between this
   * admin's read + write), the service throws 409
   * 'BUNDLE_CONCURRENT_MODIFICATION'.
   *
   * AuditLog 'BUNDLE_APPROVED' is written (INV-SEC-08 hash chain).
   * NO UnifiedTransaction — bundles do NOT create financial
   * transactions on approve/reject (per task constraint).
   *
   * 2FA required (INV-SEC-06 §9.6 — per-route `requireRecent2FA`
   * in `routes/admin.route.ts`). On 403 `2FA_REQUIRED`, the
   * caller's catch block re-opens the `TwoFactorFinancialDialog`
   * (rendered by `/admin/market/combos/page.tsx`).
   *
   * Path param: `bundleId` is the `ProductBundle.bundleId` (UUID).
   * The optional `notes` body field (1-500 chars) is recorded ONLY
   * in the AuditLog metadata (the `ProductBundle` model has no
   * `adminNotes` field per SPEC-242 §4.2).
   *
   * Spec ref: SPEC-242 §4.4 (PUT /admin/market/bundles/:id/approve),
   *           §4.3 (INV-MKT-20 admin approval gate);
   *           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06).
   */
  async approve(
    bundleId: string,
    options?: { notes?: string },
    signal?: AbortSignal,
  ): Promise<ProductBundle> {
    const body =
      options?.notes !== undefined
        ? { notes: options.notes }
        : {};
    return apiFetch<ProductBundle>(
      `/admin/market/bundles/${encodeURIComponent(bundleId)}/approve`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        signal,
      },
    );
  },
  /**
   * PUT /api/admin/market/bundles/:id/reject — reject a pending
   * bundle (INV-MKT-20). The backend atomically transitions
   * `status: 'pending_approval' → 'rejected'` via CAS. The
   * `reason` is MANDATORY (Zod `bundleRejectSchema` — 1-500 chars)
   * — the seller needs to know why their bundle was rejected so
   * they can fix + resubmit. The reason is recorded in the
   * AuditLog metadata (the `ProductBundle` model has no
   * `adminNotes` field per SPEC-242 §4.2).
   *
   * 2FA required (INV-SEC-06 §9.6 — same rationale as `approve`).
   *
   * Path param: `bundleId` is the `ProductBundle.bundleId` (UUID).
   *
   * Spec ref: SPEC-242 §4.4 (PUT /admin/market/bundles/:id/reject),
   *           §4.3 (INV-MKT-20 admin approval gate);
   *           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06).
   */
  async reject(
    bundleId: string,
    reason: string,
    signal?: AbortSignal,
  ): Promise<ProductBundle> {
    return apiFetch<ProductBundle>(
      `/admin/market/bundles/${encodeURIComponent(bundleId)}/reject`,
      {
        method: 'PUT',
        body: JSON.stringify({ reason }),
        signal,
      },
    );
  },
};
